// Multi-food meal adds whose outcome the client has not confirmed. They belong
// to the signed-in account + meal on a BackendService, not to a Meal or picker
// instance, so remounts and reopened pickers retry the same operation key.

export const MAX_MEAL_FOOD_BATCH = 100;

const operationsByService = new WeakMap();

export const accountScopeOf = service => ({
  identity: service?.authenticatedIdentity ?? null,
  generation: Number(service?.backgroundIdentityGeneration) || 0,
});

export const isSameAccountScope = (service, scope) => {
  const live = accountScopeOf(service);
  return live.identity === scope.identity && live.generation === scope.generation;
};

const operationsFor = service => {
  let operations = operationsByService.get(service);
  if (!operations) {
    operations = new Map();
    operationsByService.set(service, operations);
  }
  return operations;
};

const keyFor = (identity, mealId) => JSON.stringify([identity, String(mealId)]);

export const readFoodBatchOperation = (service, identity, mealId) => (
  service ? operationsFor(service).get(keyFor(identity, mealId)) || null : null
);

const releaseFoodBatchOperation = (service, identity, mealId, operation) => {
  const operations = operationsFor(service);
  const key = keyFor(identity, mealId);
  if (operations.get(key) === operation) operations.delete(key);
};

// Lifecycle: 'queued' (never sent) → 'pending' (attempt in flight) →
// 'unknown' (retryable with the same key) or a terminal state. Terminal
// operations leave the registry but keep their state (and committed meal) for
// anyone still holding the operation, e.g. a picker restored from it.
const TERMINAL_STATES = new Set(['committed', 'rejected', 'cancelled', 'abandoned']);
export const isSettledFoodBatchOperation = operation => TERMINAL_STATES.has(operation?.state);

export const createFoodBatchOperation = (service, identity, mealId, { operationId, payloadKey, foods, items }) => {
  const operation = { operationId, payloadKey, foods, items, state: 'queued', promise: null, meal: null, claims: new Set() };
  operationsFor(service).set(keyFor(identity, mealId), operation);
  return operation;
};

export const settleFoodBatchOperation = (service, identity, mealId, operation, state, meal = null) => {
  operation.state = state;
  if (meal) operation.meal = meal;
  releaseFoodBatchOperation(service, identity, mealId, operation);
};

export const markFoodBatchOperationUnknown = (service, identity, mealId, operation) => {
  operation.state = 'unknown';
  // Never leave a retryable operation detached from its account + meal slot.
  const operations = operationsFor(service);
  const key = keyFor(identity, mealId);
  if (!operations.has(key)) operations.set(key, operation);
};

// A claim is one submit's queued interest in sending the operation. Only when
// the last claim is dropped without anyone having sent it is a never-sent
// operation retired; an attempt adopted by another Meal instance is untouched.
export const claimFoodBatchOperation = operation => {
  const claim = {};
  operation.claims.add(claim);
  return claim;
};

export const dropFoodBatchClaim = (service, identity, mealId, operation, claim) => {
  operation.claims.delete(claim);
  if (operation.claims.size === 0 && operation.state === 'queued') {
    settleFoodBatchOperation(service, identity, mealId, operation, 'abandoned');
  }
};

// Only the server's explicit pre-commit rejection is definitive. Network
// errors, timeouts and 5xx responses may follow a committed write.
export const isRejectedFoodBatchError = error => error?.response?.data?.outcome === 'rejected';

export const foodBatchError = (outcome, message) => Object.assign(new Error(message), { foodBatchOutcome: outcome });
